<template>
    <div class="retr-current__condition-group">
        <!-- Sales goals progress bar -->
        <div
            v-if="conditionGroup.sales_result !== null && hasSalesGoals"
            class="retr-current__sales-goals-progress-bar"
        >
            <span>Osiągnięty próg</span>
            <div class="retr-current__sales-goals-progress-bar-container">
                <retr-sales-goals-progress-bar
                    :thresholds="conditionGroup.sales_goals"
                    :current-progress="conditionGroup.sales_result"
                />
            </div>
        </div>

        <!-- Condition types (sorted by order) -->
        <div
            v-for="(conditionType, conditionTypeIndex) in sortedConditionTypes"
            :key="`conditionType-${conditionTypeIndex}`"
        >
            <!-- Individual conditions -->
            <retr-current-condition-item
                v-for="(condition, conditionIndex) in conditionType.list"
                :key="`condition-${conditionIndex}`"
                :condition="condition"
                :minimum-sku="conditionType.minimum_sku"
                :manufacturer-name="manufacturerName"
                :condition-type="conditionType.condition_type"
                :is-sales-goal-condition-realized="condition.isSalesGoalConditionRealized"
            />
        </div>
    </div>
</template>

<script>
import RetrSalesGoalsProgressBar from '@/components/my-business/retr/RetrSalesGoalsProgressBar.vue'
import RetrCurrentConditionItem from '@/components/my-business/retr/RetrCurrentConditionItem.vue'

export default {
    name: 'RetrCurrentConditionGroup',
    components: {
        RetrSalesGoalsProgressBar,
        RetrCurrentConditionItem
    },
    props: {
        conditionGroup: {
            type: Object,
            required: true
        },
        manufacturerName: {
            type: String,
            default: ''
        }
    },
    computed: {
        hasSalesGoals() {
            return Array.isArray(this.conditionGroup.sales_goals)
        },
        /**
         * Sort condition types and map conditions with sales goal realization status
         * @returns {Array} Sorted array of condition types with enhanced conditions
         */
        sortedConditionTypes() {
            if (!this.conditionGroup.list || !Array.isArray(this.conditionGroup.list)) {
                return []
            }

            const sorted = [...this.conditionGroup.list].sort((a, b) =>
                a.condition_order_group - b.condition_order_group
            )

            // If no sales goals present, no need to process further
            // This sorting is essential for threshold logic: when a higher sales threshold is reached,
            // all previous lower thresholds must also be marked as realized
            if (!this.hasSalesGoals) {
                return sorted
            }

            // Find the first conditionType where list[0].is_condition_realized === true
            const firstRealizedConditionTypeIndex = sorted.findIndex(
                conditionType => conditionType.list?.[0]?.is_condition_realized === true
            )

            // If no condition is realized, no need to enhance the data
            if (firstRealizedConditionTypeIndex === -1) {
                return sorted
            }

            // Map condition types with sales goal status
            return sorted.map((conditionType, conditionTypeIndex) => {
                if (!conditionType.list || conditionType.list.length === 0) {
                    return conditionType
                }

                // All conditions in conditionTypes up to and including firstRealizedConditionTypeIndex get true
                const isSalesGoalRealized = conditionTypeIndex <= firstRealizedConditionTypeIndex

                const enhancedList = conditionType.list.map(condition => ({
                    ...condition,
                    isSalesGoalConditionRealized: isSalesGoalRealized
                }))

                return {
                    ...conditionType,
                    list: enhancedList
                }
            })
        }
    }
}
</script>

<style lang="scss">
.retr-current {
    &__condition-group {
        padding: 0.5rem 0;
        &:not(:last-child) {
            border-bottom: 1px solid var(--color-gray-300);
        }
    }

    &__sales-goals-progress-bar {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0;
        justify-content: space-between;
        padding-right: calc(var(--button-space) + 24px);
    }

    &__sales-goals-progress-bar-container {
        width: 240px;
    }
}
</style>
